// iter623 — Phase G tools dialog: bulk-reconcile + smart-match preview
// + cash-position chart. Single component embedded into the existing
// Bank Reconciliation flows so users don't need to learn a new page.

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, Check, X, TrendingUp, Zap, Layers, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';

export default function PhaseGToolsDialog({ open, onClose, statementId, statementName }) {
  const [tab, setTab] = useState('bulk');
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // iter624 — Let the user pick from recent statements (was hardcoded to "latest").
  const [statements, setStatements] = useState([]);
  const [activeStmtId, setActiveStmtId] = useState(statementId);
  // Smart-match — iter624 exposes the score weights so users can
  // tune the matcher to their data without a code change.
  // iter625 — Production-tuned defaults (loose: tolerance=2, window=5).
  // Overridden by per-tenant defaults loaded via loadDefaultWeights().
  const [tolerance, setTolerance] = useState(2.0);
  const [windowDays, setWindowDays] = useState(5);
  const [amountWeight, setAmountWeight] = useState(1.0);
  const [dateWeight, setDateWeight] = useState(0.1);
  const [matches, setMatches] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [accept, setAccept] = useState(new Set());
  // Cash chart
  const [chart, setChart] = useState(null);

  const loadStmt = async () => {
    if (!activeStmtId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/bank-statements/${activeStmtId}`);
      setStmt(data);
      setSelected(new Set());
    } catch (e) {
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  // iter624 — Recent-statements dropdown.
  const loadStatementsList = async () => {
    try {
      const { data } = await api.get('/bank-statements');
      setStatements(Array.isArray(data) ? data.slice(0, 50) : []);
    } catch { /* */ }
  };

  const loadChart = async () => {
    if (!activeStmtId) return;
    try {
      const { data } = await api.get('/recon-inbox/cash-position', { params: { statement_id: activeStmtId } });
      setChart(data);
    } catch { /* */ }
  };

  useEffect(() => { if (open) { loadStatementsList(); loadDefaultWeights(); } /* eslint-disable-next-line */ }, [open]);
  useEffect(() => { setActiveStmtId(statementId); }, [statementId]);
  useEffect(() => { if (open && activeStmtId) { loadStmt(); loadChart(); setMatches(null); } }, [open, activeStmtId]);

  // iter625 — Per-tenant default smart-match weights (GET/PUT round-trip).
  const [savingDefaults, setSavingDefaults] = useState(false);
  const loadDefaultWeights = async () => {
    try {
      const { data } = await api.get('/recon-inbox/default-weights');
      if (data?.amount_tolerance != null) setTolerance(data.amount_tolerance);
      if (data?.date_window_days != null) setWindowDays(data.date_window_days);
      if (data?.amount_weight != null) setAmountWeight(data.amount_weight);
      if (data?.date_weight != null) setDateWeight(data.date_weight);
    } catch { /* defaults already in state */ }
  };
  const saveDefaultWeights = async () => {
    setSavingDefaults(true);
    try {
      await api.put('/recon-inbox/default-weights', {
        amount_tolerance: Number(tolerance),
        date_window_days: Number(windowDays),
        amount_weight: Number(amountWeight),
        date_weight: Number(dateWeight),
      });
      toast.success('Saved as default for this tenant');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save defaults');
    } finally { setSavingDefaults(false); }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAllUnreconciled = () => {
    const u = new Set((stmt?.transactions || []).filter(t => !t.reconciled).map(t => t.id));
    setSelected(u);
  };

  const bulkReconcile = async (target) => {
    if (selected.size === 0) { toast.error('No rows selected'); return; }
    try {
      const { data } = await api.post('/recon-inbox/bulk-reconcile', {
        statement_id: activeStmtId,
        transaction_ids: Array.from(selected),
        reconciled: target,
      });
      toast.success(`${data.updated} row(s) ${target ? 'reconciled' : 'un-reconciled'} by ${data.actor}`);
      setSelected(new Set());
      loadStmt();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reconcile failed');
    }
  };

  const previewSmartMatch = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/recon-inbox/smart-match', {
        statement_id: activeStmtId,
        amount_tolerance: Number(tolerance),
        date_window_days: Number(windowDays),
        amount_weight: Number(amountWeight),
        date_weight: Number(dateWeight),
        apply: false,
      });
      setMatches(data);
      // Auto-accept all by default — user can untick.
      setAccept(new Set(data.matches.map(m => m.txn_id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const applySmartMatch = async () => {
    if (!matches || matches.matches.length === 0) return;
    // Apply only the accepted ones via bulk-reconcile (mirrors the
    // smart-match path but respects user de-selection from the preview).
    const txnIds = matches.matches.filter(m => accept.has(m.txn_id)).map(m => m.txn_id);
    if (txnIds.length === 0) { toast.error('No matches accepted'); return; }
    setApplying(true);
    try {
      const { data } = await api.post('/recon-inbox/bulk-reconcile', {
        statement_id: activeStmtId,
        transaction_ids: txnIds,
        reconciled: true,
        notes: 'Smart-match (reviewed)',
      });
      toast.success(`${data.updated} match(es) applied`);
      setMatches(null);
      setAccept(new Set());
      loadStmt();
      loadChart();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  const unreconciledCount = (stmt?.transactions || []).filter(t => !t.reconciled).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90dvh] overflow-y-auto" data-testid="phase-g-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Zap size={18} className="text-amber-500" />
            Phase G tools · <span className="text-muted-foreground text-sm font-normal">{statementName || statementId}</span>
          </DialogTitle>
        </DialogHeader>

        {/* iter624 — Statement picker. Defaults to the one passed in,
            but lets the user switch without re-opening the dialog. */}
        {statements.length > 0 && (
          <div className="flex items-center gap-2 -mt-2">
            <label className="text-[11px] text-muted-foreground shrink-0">Statement:</label>
            <select
              value={activeStmtId || ''}
              onChange={(e) => setActiveStmtId(e.target.value)}
              className="text-[12px] rounded-lg border border-border px-2 py-1.5 bg-white flex-1 max-w-md"
              data-testid="phase-g-statement-picker"
            >
              {statements.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.bank_name || 'Bank'} · {s.statement_date || s.period_end || s.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tab strip */}
        <div className="flex border-b border-border">
          {[
            ['bulk', 'Bulk reconcile', Layers, unreconciledCount],
            ['smart', 'Smart-match', Sparkles, null],
            ['chart', 'Cash position', TrendingUp, null],
          ].map(([k, label, Icon, badge]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${tab === k ? 'border-amber-500 text-amber-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`phase-g-tab-${k}`}
            >
              <Icon size={14} /> {label}
              {badge != null && badge > 0 && (
                <span className="bg-rose-100 text-rose-700 text-[10px] px-1.5 py-0.5 rounded-full">{badge}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
        ) : (
          <div className="pt-3">
            {/* --- BULK RECONCILE --- */}
            {tab === 'bulk' && (
              <div className="space-y-2" data-testid="phase-g-bulk-pane">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={selectAllUnreconciled} data-testid="phase-g-select-all">
                    Select all unreconciled ({unreconciledCount})
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Clear</Button>
                  <div className="text-[12px] text-muted-foreground ml-auto">{selected.size} selected</div>
                  <Button size="sm" disabled={selected.size === 0} onClick={() => bulkReconcile(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="phase-g-bulk-reconcile">
                    <Check size={14} className="mr-1" /> Mark {selected.size} reconciled
                  </Button>
                  <Button size="sm" disabled={selected.size === 0} variant="outline" onClick={() => bulkReconcile(false)} data-testid="phase-g-bulk-unreconcile">
                    <X size={14} className="mr-1" /> Un-reconcile {selected.size}
                  </Button>
                </div>
                <div className="rounded-lg border border-border overflow-y-auto max-h-[40dvh]">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-stone-50 sticky top-0">
                        <tr><th className="p-2"></th><th className="p-2 text-left">Date</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Credit</th><th className="p-2 text-right">Debit</th><th className="p-2">Status</th></tr>
                      </thead>
                      <tbody>
                        {(stmt?.transactions || []).map(t => (
                          <tr key={t.id} className="border-t border-border/40 hover:bg-stone-50">
                            <td className="p-2"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} disabled={t.reconciled} /></td>
                            <td className="p-2 whitespace-nowrap">{t.date}</td>
                            <td className="p-2 truncate max-w-xs" title={t.description}>{t.description}</td>
                            <td className="p-2 text-right text-emerald-700">{t.credit ? `${cur()} ${Number(t.credit).toLocaleString()}` : '-'}</td>
                            <td className="p-2 text-right text-rose-700">{t.debit ? `${cur()} ${Number(t.debit).toLocaleString()}` : '-'}</td>
                            <td className="p-2 text-center">{t.reconciled ? <Check size={12} className="text-emerald-600 inline" /> : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* --- SMART MATCH --- */}
            {tab === 'smart' && (
              <div className="space-y-3" data-testid="phase-g-smart-pane">
                <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                  <div>
                    <label className="text-[11px] font-semibold text-amber-900">Amount tolerance</label>
                    <Input type="number" step="0.01" value={tolerance} onChange={e => setTolerance(e.target.value)} className="h-8" data-testid="phase-g-tolerance" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-amber-900">Date window (days)</label>
                    <Input type="number" value={windowDays} onChange={e => setWindowDays(e.target.value)} className="h-8" data-testid="phase-g-window" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-amber-900" title="Multiplier on absolute amount difference">Amount weight</label>
                    <Input type="number" step="0.1" value={amountWeight} onChange={e => setAmountWeight(e.target.value)} className="h-8" data-testid="phase-g-amount-weight" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-amber-900" title="Multiplier on date offset (per day)">Date weight</label>
                    <Input type="number" step="0.05" value={dateWeight} onChange={e => setDateWeight(e.target.value)} className="h-8" data-testid="phase-g-date-weight" />
                  </div>
                  <Button onClick={previewSmartMatch} disabled={previewing} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="phase-g-smart-preview">
                    {previewing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Sparkles size={14} className="mr-1" />} Preview
                  </Button>
                </div>
                <div className="flex items-center justify-end -mt-1">
                  <Button variant="outline" size="sm" onClick={saveDefaultWeights} disabled={savingDefaults} className="text-[11px] h-7" data-testid="phase-g-save-default-weights">
                    {savingDefaults ? <Loader2 className="animate-spin mr-1" size={12} /> : <Check size={12} className="mr-1" />} Save as default for this tenant
                  </Button>
                </div>
                {matches && (
                  <>
                    <div className="text-[12px] flex items-center justify-between flex-wrap gap-2">
                      <span><strong>{matches.matches.length}</strong> matches found · {accept.size} accepted</span>
                      <Button size="sm" onClick={applySmartMatch} disabled={applying || accept.size === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="phase-g-smart-apply">
                        {applying ? <Loader2 className="animate-spin mr-1" size={14} /> : <Check size={14} className="mr-1" />} Apply {accept.size} match(es)
                      </Button>
                    </div>
                    {matches.matches.length === 0 ? (
                      <div className="text-center text-muted-foreground py-6 italic text-[12px]">
                        No matches found. Try widening the amount tolerance or date window.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-border overflow-y-auto max-h-[40dvh]">
                        <div className="overflow-x-auto">
                          <table className="w-full text-[12px]">
                            <thead className="bg-stone-50 sticky top-0">
                              <tr>
                                <th className="p-2 w-8"></th>
                                <th className="p-2 text-left">Bank line</th>
                                <th className="p-2 text-left">→ App sale</th>
                                <th className="p-2 text-right">Diff</th>
                                <th className="p-2">Days</th>
                                <th className="p-2">Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              {matches.matches.map(m => (
                                <tr key={m.txn_id} className={`border-t border-border/40 ${accept.has(m.txn_id) ? 'bg-emerald-50/30' : 'opacity-60'}`}>
                                  <td className="p-2">
                                    <input type="checkbox" checked={accept.has(m.txn_id)} onChange={() => {
                                      setAccept(p => { const n = new Set(p); n.has(m.txn_id) ? n.delete(m.txn_id) : n.add(m.txn_id); return n; });
                                    }} data-testid={`phase-g-match-accept-${m.txn_id}`} />
                                  </td>
                                  <td className="p-2">{m.txn_date} · <strong>{cur()} {Number(m.txn_amount).toLocaleString()}</strong></td>
                                  <td className="p-2">{m.sale_date} · {cur()} {Number(m.sale_total).toLocaleString()}<div className="text-[10px] text-muted-foreground">{m.customer}</div></td>
                                  <td className="p-2 text-right">{cur()} {Math.abs(m.sale_total - m.txn_amount).toFixed(2)}</td>
                                  <td className="p-2 text-center">{m.date_offset >= 0 ? '+' : ''}{m.date_offset}</td>
                                  <td className="p-2 text-center">{m.score}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground italic flex items-start gap-1">
                      <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                      Lower score = better match. Uncheck rows you don&apos;t want to apply.
                    </div>
                  </>
                )}
                {!matches && (
                  <div className="text-center text-muted-foreground py-8 italic text-[12px]">
                    Set tolerance + window, then click <strong>Preview matches</strong>. Nothing is written until you click <strong>Apply</strong>.
                  </div>
                )}
              </div>
            )}

            {/* --- CASH POSITION CHART --- */}
            {tab === 'chart' && (
              <div className="space-y-3" data-testid="phase-g-chart-pane">
                {!chart || chart.points.length === 0 ? (
                  <div className="text-center text-muted-foreground py-12 italic text-[12px]">No transactions to chart.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
                      <div className="bg-sky-50 rounded-lg p-2"><div className="text-[10px] uppercase text-sky-700 font-bold">Opening</div><div className="font-bold">{cur()} {chart.opening_balance.toLocaleString()}</div></div>
                      <div className="bg-emerald-50 rounded-lg p-2"><div className="text-[10px] uppercase text-emerald-700 font-bold">Closing</div><div className="font-bold">{cur()} {chart.closing_balance.toLocaleString()}</div></div>
                      <div className={`rounded-lg p-2 ${(chart.closing_balance - chart.opening_balance) >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                        <div className="text-[10px] uppercase font-bold">Net change</div>
                        <div className={`font-bold ${(chart.closing_balance - chart.opening_balance) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {(chart.closing_balance - chart.opening_balance >= 0 ? '+' : '')}{cur()} {(chart.closing_balance - chart.opening_balance).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="h-72 w-full">
                      <ResponsiveContainer>
                        <AreaChart data={chart.points}>
                          <defs>
                            <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
                              <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${cur()} ${(v / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} labelStyle={{ fontWeight: 'bold' }} />
                          <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
                          <Area type="monotone" dataKey="running_balance" stroke="#06b6d4" fill="url(#cashFill)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
